import { useMemo } from 'react'
import { ProbateEstatePerson } from 'lib/models/probate-estate'
import { usePeople } from './helpers/people-context'
import {
  type AutomatedFormState,
  type ProbateCheckProvider,
  ActingExecutorsChecks,
} from './referral/acting-executors-checks'
import { RenouncingExecutorsChecks } from './referral/renouncing-executors-checks'
import {
  probateEstatePeopleRenouncingExecutors,
  probateEstatePeopleRequiringChecks,
} from './helpers/complete-sections/section-referral'
import { WORKFLOW_STATUS } from 'lib/models/kyc-check-run'
import { PROBATE_SIGNATURE_REQUEST_DOCUMENTS } from 'lib/models/probate-estate'
import {
  getActiveSignatureRequest,
  getSignatureRequestDates,
} from './helpers/signature-request-helpers'
import { pollUntil } from 'utils/async'

export default function ProbateEstatePeopleTermsAndIdChecks({
  isInHouseReferral,
}: {
  isInHouseReferral: boolean
}) {
  const {
    people,
    updatePerson,
    updatePersonSignatureRequest,
    sendDocusignSignatureRequest,
    createPersonKycCheck,
    listPersonKycChecks,
  } = usePeople()

  const PROVIDERS: ProbateCheckProvider[] = useMemo(() => {
    const getDocusignState = (
      person: ProbateEstatePerson | undefined
    ): AutomatedFormState => {
      if (!person) return { sentAt: null, receivedAt: null }

      const activeRequest = getActiveSignatureRequest(
        person.attributes.signatureRequests,
        PROBATE_SIGNATURE_REQUEST_DOCUMENTS.TERMS_AND_CONDITIONS,
        'auto'
      )

      return getSignatureRequestDates(activeRequest)
    }

    const getOnfidoState = async (
      personId: number
    ): Promise<AutomatedFormState> => {
      const kycChecks = await listPersonKycChecks(personId)
      if (!kycChecks.length) {
        return { sentAt: null, receivedAt: null }
      }
      const [mostRecentCheck] = kycChecks
      return {
        receivedAt:
          mostRecentCheck.attributes.status === WORKFLOW_STATUS.approved
            ? mostRecentCheck.attributes.updatedAt
            : null,
        sentAt: mostRecentCheck.attributes.createdAt,
      }
    }

    return [
      {
        provider: 'docusign' as const,
        formTitle: 'Send Terms and Conditions',
        automatedForm: {
          title: 'Tell them to expect it',
          description: 'Send the Docusign link now?',
          state: async (personId: number): Promise<AutomatedFormState> => {
            return getDocusignState(people.find((p) => p.id === personId))
          },
          submit: async (personId: number): Promise<AutomatedFormState> => {
            const updatedPerson = await sendDocusignSignatureRequest({
              personId,
              documentName:
                PROBATE_SIGNATURE_REQUEST_DOCUMENTS.TERMS_AND_CONDITIONS,
            })
            return getDocusignState(updatedPerson)
          },
        },
        manualForm: {
          title: 'Print and post the documents',
          description:
            'Tick to confirm when documents are sent, and when signed copies are recieved.',
          sendToggle: (personId: number, date: Date | null) =>
            updatePersonSignatureRequest({
              personId,
              sent: date !== null,
              signed: false,
              documentName:
                PROBATE_SIGNATURE_REQUEST_DOCUMENTS.TERMS_AND_CONDITIONS,
            }),
          confirmToggle: (personId: number, date: Date | null) =>
            updatePersonSignatureRequest({
              personId,
              sent: true,
              signed: date !== null,
              documentName:
                PROBATE_SIGNATURE_REQUEST_DOCUMENTS.TERMS_AND_CONDITIONS,
            }),
          confirmedAction: 'signed',
        },
        detectMode: (personId: number) => {
          const person = people.find((p) => p.id === personId)
          if (!person) return null

          const activeAuto = getActiveSignatureRequest(
            person.attributes.signatureRequests,
            PROBATE_SIGNATURE_REQUEST_DOCUMENTS.TERMS_AND_CONDITIONS,
            'auto'
          )
          const activeManual = getActiveSignatureRequest(
            person.attributes.signatureRequests,
            PROBATE_SIGNATURE_REQUEST_DOCUMENTS.TERMS_AND_CONDITIONS,
            'manual'
          )

          if (activeAuto && !activeManual) return 'auto'
          if (activeManual && !activeAuto) return 'manual'
          if (!activeAuto && !activeManual) return null

          // Both active (edge case) - compare by createdAt
          if (activeAuto && activeManual) {
            return new Date(activeAuto.createdAt) >=
              new Date(activeManual.createdAt)
              ? 'auto'
              : 'manual'
          }

          return null
        },
      },
      {
        provider: 'onfido' as const,
        formTitle: 'Send KYC and AML checks',
        automatedForm: {
          title: 'Tell them to expect it',
          description: 'Send the Onfido link now?',
          state: (personId: number) => getOnfidoState(personId),
          submit: async (personId: number): Promise<AutomatedFormState> => {
            await createPersonKycCheck(personId)
            // The check is created asynchronously via BullMQ, so we need to
            // poll until the state is updated
            return pollUntil(
              () => getOnfidoState(personId),
              (state) => state.sentAt !== null,
              { maxWaitTimeMs: 15000, initialDelayMs: 2000, incrementMs: 500 }
            )
          },
        },
        manualForm: {
          title: 'Setup the check in Onfido',
          description:
            "Tick to confirm when checks are sent, and when they're completed.",
          sendToggle: (personId: number, date: Date | null) =>
            updatePerson({
              id: personId,
              attributes: {
                kycChecksSentAt: date,
              },
            }),
          confirmToggle: (personId: number, date: Date | null) =>
            updatePerson({
              id: personId,
              attributes: {
                kycChecksPassedAt: date,
              },
            }),
          confirmedAction: 'received',
        },
        detectMode: (
          _personId: number,
          autoState: AutomatedFormState | null,
          manualSentValue: Date | null
        ) => {
          // Onfido: simple date-based comparison
          const autoSentAt = autoState?.sentAt ?? null
          if (!autoSentAt && !manualSentValue) return null
          if (autoSentAt && !manualSentValue) return 'auto'
          if (!autoSentAt && manualSentValue) return 'manual'
          // Both exist - compare dates
          if (autoSentAt && manualSentValue) {
            return autoSentAt >= manualSentValue ? 'auto' : 'manual'
          }
          return null
        },
      },
    ]
  }, [
    people,
    updatePerson,
    updatePersonSignatureRequest,
    sendDocusignSignatureRequest,
    createPersonKycCheck,
    listPersonKycChecks,
  ])

  const peopleRequiringChecks = useMemo(
    () => probateEstatePeopleRequiringChecks(people),
    [people]
  )

  const renouncingExecutors = useMemo(
    () => probateEstatePeopleRenouncingExecutors(people),
    [people]
  )

  if (!people.length || !isInHouseReferral) {
    return null
  }

  return (
    <>
      <ActingExecutorsChecks
        people={peopleRequiringChecks}
        providers={PROVIDERS}
      />
      <RenouncingExecutorsChecks people={renouncingExecutors} />
    </>
  )
}
